import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  fetchWeekdayCheckInAndCheckOutTimesDataBySeasonRateId,
  fetchWeekdayCheckInRulesBySeasonRateId,
  fetchWeekdayCheckOutRulesBySeasonRateId,
  fetchWeekdayMinimumStayRulesDataBySeasonRateId,
  fetchWeekdayPriceChangeRulesDataBySeasonRateId,
  updateSeasonRateWeekdayCheckInAndCheckOutRules,
  updateSeasonRateWeekdayCheckInAndCheckOutTimesData,
  updateSeasonRateWeekdayMinimumStayRulesData,
  updateSeasonRateWeekdayPriceChangeRulesData,
} from '../../common/api/seasonRateRulesService';
import {
  WeekdayCheckInAndCheckOutTimeItemsData,
  WeekdayCheckInRuleItem,
  WeekdayCheckOutRuleItem,
  WeekdayMinimumStayRuleItemsData,
  WeekdayPriceChangeRuleItemsData,
} from '../../common/types/rates.types';

export interface SeasonRateWeekdayRulesState {
  weekdayCheckInRuleItems: WeekdayCheckInRuleItem[];
  weekdayCheckOutRuleItems: WeekdayCheckOutRuleItem[];
  weekdayMinimumStayRuleItemsData: WeekdayMinimumStayRuleItemsData;
  weekdayPriceChangeRuleItemsData: WeekdayPriceChangeRuleItemsData;
  weekdayCheckInCheckOutTimeItemsData: WeekdayCheckInAndCheckOutTimeItemsData;
  weekdayCheckInRuleItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  weekdayCheckOutRuleItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  weekdayMinimumStayRuleItemsDataStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  weekdayPriceChangeRuleItemsDataStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  weekdayCheckInCheckOutTimeItemsDataStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

const initialState: SeasonRateWeekdayRulesState = {
  weekdayCheckInRuleItems: [],
  weekdayCheckOutRuleItems: [],
  weekdayMinimumStayRuleItemsData: {
    seasonRateId: '',
    weekdayMinimumDurations: [],
  },
  weekdayPriceChangeRuleItemsData: {
    seasonRateId: '',
    weekdayPriceChanges: [],
  },
  weekdayCheckInCheckOutTimeItemsData: {
    defaultRateId: '',
    checkInTimes: [],
    checkOutTimes: [],
  },
  weekdayCheckInRuleItemsStatus: LOADING_STATUS.IDLE,
  weekdayCheckOutRuleItemsStatus: LOADING_STATUS.IDLE,
  weekdayMinimumStayRuleItemsDataStatus: LOADING_STATUS.IDLE,
  weekdayPriceChangeRuleItemsDataStatus: LOADING_STATUS.IDLE,
  weekdayCheckInCheckOutTimeItemsDataStatus: LOADING_STATUS.IDLE,
};

export const getWeekdayCheckInRulesAsync = createAsyncThunk(
  'seasonRateWeekdayRules/getWeekdayCheckInRules',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchWeekdayCheckInRulesBySeasonRateId(seasonRateId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getWeekdayCheckOutRulesAsync = createAsyncThunk(
  'seasonRateWeekdayRules/getWeekdayCheckOutRules',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchWeekdayCheckOutRulesBySeasonRateId(seasonRateId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getWeekdayMinimumStayRulesDataAsync = createAsyncThunk(
  'seasonRateWeekdayRules/getWeekdayMinimumStayRulesData',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchWeekdayMinimumStayRulesDataBySeasonRateId(seasonRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getWeekdayPriceChangeRulesDataAsync = createAsyncThunk(
  'seasonRateWeekdayRules/getWeekdayPriceChangeRulesData',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchWeekdayPriceChangeRulesDataBySeasonRateId(seasonRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getWeekdayCheckInAndCheckOutTimesDataAsync = createAsyncThunk(
  'seasonRateWeekdayRules/getWeekdayCheckInAndCheckOutTimesData',
  async (seasonRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchWeekdayCheckInAndCheckOutTimesDataBySeasonRateId(
          seasonRateId,
        );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSeasonRateWeekdayCheckInAndCheckOutRulesAsync =
  createAsyncThunk(
    'seasonRateWeekdayRules/updateSeasonRateWeekdayCheckInAndCheckOutRules',
    /* TODO Replace any/ CORE-1006 */
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    async (rulesData: any, { rejectWithValue }) => {
      try {
        const response = await updateSeasonRateWeekdayCheckInAndCheckOutRules(
          rulesData.seasonRateId,
          rulesData.weekdayCheckInRules,
          rulesData.weekdayCheckOutRules,
        );
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response.data);
      }
    },
  );

export const updateSeasonRateWeekdayMinimumStayRulesDataAsync =
  createAsyncThunk(
    'seasonRateWeekdayRules/updateSeasonRateWeekdayMinimumStayRulesData',
    async (rulesData: WeekdayMinimumStayRuleItemsData, { rejectWithValue }) => {
      try {
        const response =
          await updateSeasonRateWeekdayMinimumStayRulesData(rulesData);
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response.data);
      }
    },
  );

export const updateSeasonRateWeekdayPriceChangeRulesDataAsync =
  createAsyncThunk(
    'seasonRateWeekdayRules/updateSeasonRateWeekdayPriceChangeRulesData',
    async (rulesData: WeekdayPriceChangeRuleItemsData, { rejectWithValue }) => {
      try {
        const response =
          await updateSeasonRateWeekdayPriceChangeRulesData(rulesData);
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response.data);
      }
    },
  );

export const updateSeasonRateWeekdayCheckInAndCheckOutTimesDataAsync =
  createAsyncThunk(
    'seasonRateWeekdayRules/updateSeasonRateWeekdayCheckInAndCheckOutTimesData',
    async (
      rulesData: WeekdayCheckInAndCheckOutTimeItemsData,
      { rejectWithValue },
    ) => {
      try {
        const response =
          await updateSeasonRateWeekdayCheckInAndCheckOutTimesData(rulesData);
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response.data);
      }
    },
  );

const seasonRateWeekdayRulesSlice = createSlice({
  name: 'seasonRateWeekdayRules',
  initialState,
  reducers: {
    addWeekdayCheckInRestriction: (state, action) => {
      state.weekdayCheckInRuleItems.push(action.payload);
    },
    removeWeekdayCheckInRestriction: (state, action) => {
      const itemId = action.payload;
      state.weekdayCheckInRuleItems = state.weekdayCheckInRuleItems.filter(
        item => item.weekday !== itemId,
      );
    },
    addWeekdayCheckOutRestriction: (state, action) => {
      state.weekdayCheckOutRuleItems.push(action.payload);
    },
    removeWeekdayCheckOutRestriction: (state, action) => {
      const itemId = action.payload;
      state.weekdayCheckOutRuleItems = state.weekdayCheckOutRuleItems.filter(
        item => item.weekday !== itemId,
      );
    },
    updateCopyableForWeekdayCheckInRestrictionData: (state, action) => {
      const { copyable } = action.payload;
      state.weekdayCheckInRuleItems = state.weekdayCheckInRuleItems.map(
        item => ({ ...item, isCopyable: copyable }),
      );
    },
    updateCopyableForWeekdayCheckOutRestrictionData: (state, action) => {
      const { copyable } = action.payload;
      state.weekdayCheckOutRuleItems = state.weekdayCheckOutRuleItems.map(
        item => ({ ...item, isCopyable: copyable }),
      );
    },
    updateActiveForWeekdayCheckInRestrictionData: (state, action) => {
      const { active } = action.payload;
      state.weekdayCheckInRuleItems = state.weekdayCheckInRuleItems.map(
        item => ({ ...item, isActive: active }),
      );
    },
    updateActiveForWeekdayCheckOutRestrictionData: (state, action) => {
      const { active } = action.payload;
      state.weekdayCheckOutRuleItems = state.weekdayCheckOutRuleItems.map(
        item => ({ ...item, isActive: active }),
      );
    },
    addWeekdayMinimumStayRestriction: (state, action) => {
      state.weekdayMinimumStayRuleItemsData.weekdayMinimumDurations.push(
        action.payload,
      );
    },
    updateWeekdayMinimumStayRestriction: (state, action) => {
      const updatedRule = action.payload;
      const index =
        state.weekdayMinimumStayRuleItemsData.weekdayMinimumDurations.findIndex(
          rule => rule.weekday === updatedRule.weekday,
        );
      state.weekdayMinimumStayRuleItemsData.weekdayMinimumDurations[index] =
        action.payload;
    },
    removeWeekdayMinimumStayRestriction: (state, action) => {
      const weekday = action.payload;
      state.weekdayMinimumStayRuleItemsData.weekdayMinimumDurations =
        state.weekdayMinimumStayRuleItemsData.weekdayMinimumDurations.filter(
          item => item.weekday !== weekday,
        );
    },
    updateWeekdayMinimumStayRuleItemsData: (state, action) => {
      state.weekdayMinimumStayRuleItemsData = action.payload;
    },
    addWeekdayPriceChangeRule: (state, action) => {
      state.weekdayPriceChangeRuleItemsData.weekdayPriceChanges.push(
        action.payload,
      );
    },
    updateWeekdayPriceChangeRule: (state, action) => {
      const updatedRule = action.payload;
      const index =
        state.weekdayPriceChangeRuleItemsData.weekdayPriceChanges.findIndex(
          rule => rule.weekday === updatedRule.weekday,
        );
      state.weekdayPriceChangeRuleItemsData.weekdayPriceChanges[index] =
        action.payload;
    },
    removeWeekdayPriceChangeRule: (state, action) => {
      const weekday = action.payload;
      state.weekdayPriceChangeRuleItemsData.weekdayPriceChanges =
        state.weekdayPriceChangeRuleItemsData.weekdayPriceChanges.filter(
          item => item.weekday !== weekday,
        );
    },
    addWeekdayCheckInTime: (state, action) => {
      state.weekdayCheckInCheckOutTimeItemsData.checkInTimes.push(
        action.payload,
      );
    },
    addWeekdayCheckOutTime: (state, action) => {
      state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes.push(
        action.payload,
      );
    },
    addWeekdayCheckInTimeByDuration: (state, action) => {
      const { weekday, data } = action.payload;
      data.weekday = weekday;

      const index =
        state.weekdayCheckInCheckOutTimeItemsData.checkInTimes.findIndex(
          checkIn => checkIn.weekday === weekday,
        );

      state.weekdayCheckInCheckOutTimeItemsData.checkInTimes[
        index
      ].timesByDuration.push(data);
    },
    addWeekdayCheckOutTimeByDuration: (state, action) => {
      const { weekday, data } = action.payload;
      data.weekday = weekday;

      const index =
        state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes.findIndex(
          checkOut => checkOut.weekday === weekday,
        );

      state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes[
        index
      ].timesByDuration.push(data);
    },
    updateWeekdayCheckInTime: (state, action) => {
      const updated = action.payload;
      const index =
        state.weekdayCheckInCheckOutTimeItemsData.checkInTimes.findIndex(
          rule => rule.weekday === updated.weekday,
        );
      state.weekdayCheckInCheckOutTimeItemsData.checkInTimes[index] =
        action.payload;
    },
    updateWeekdayCheckOutTime: (state, action) => {
      const updated = action.payload;
      const index =
        state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes.findIndex(
          rule => rule.weekday === updated.weekday,
        );
      state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes[index] =
        action.payload;
    },
    removeWeekdayCheckInTime: (state, action) => {
      const weekday = action.payload;
      state.weekdayCheckInCheckOutTimeItemsData.checkInTimes =
        state.weekdayCheckInCheckOutTimeItemsData.checkInTimes.filter(
          item => item.weekday !== weekday,
        );
    },
    removeWeekdayCheckOutTime: (state, action) => {
      const weekday = action.payload;
      state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes =
        state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes.filter(
          item => item.weekday !== weekday,
        );
    },
    removeWeekdayCheckInTimeByDuration: (state, action) => {
      const { id, weekday } = action.payload;

      const indexForCheckInTime =
        state.weekdayCheckInCheckOutTimeItemsData.checkInTimes.findIndex(
          checkIn => checkIn.weekday === weekday,
        );

      state.weekdayCheckInCheckOutTimeItemsData.checkInTimes[
        indexForCheckInTime
      ].timesByDuration =
        state.weekdayCheckInCheckOutTimeItemsData.checkInTimes[
          indexForCheckInTime
        ].timesByDuration.filter(item => item.id !== id);
    },
    removeWeekdayCheckOutTimeByDuration: (state, action) => {
      const { id, weekday } = action.payload;

      const indexForCheckInTime =
        state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes.findIndex(
          checkOut => checkOut.weekday === weekday,
        );

      state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes[
        indexForCheckInTime
      ].timesByDuration =
        state.weekdayCheckInCheckOutTimeItemsData.checkOutTimes[
          indexForCheckInTime
        ].timesByDuration.filter(item => item.id !== id);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getWeekdayCheckInRulesAsync.pending, (state, action) => {
        state.weekdayCheckInRuleItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getWeekdayCheckInRulesAsync.fulfilled, (state, action) => {
        state.weekdayCheckInRuleItems = action.payload;
        state.weekdayCheckInRuleItemsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getWeekdayCheckInRulesAsync.rejected, (state, action) => {
        state.weekdayCheckInRuleItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getWeekdayCheckOutRulesAsync.fulfilled, (state, action) => {
        state.weekdayCheckOutRuleItems = action.payload;
        state.weekdayCheckOutRuleItemsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getWeekdayCheckOutRulesAsync.pending, (state, action) => {
        state.weekdayCheckOutRuleItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getWeekdayCheckOutRulesAsync.rejected, (state, action) => {
        state.weekdayCheckOutRuleItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(
        getWeekdayMinimumStayRulesDataAsync.fulfilled,
        (state, action) => {
          state.weekdayMinimumStayRuleItemsData = action.payload;
          state.weekdayMinimumStayRuleItemsDataStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(getWeekdayMinimumStayRulesDataAsync.pending, (state, action) => {
        state.weekdayMinimumStayRuleItemsDataStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getWeekdayMinimumStayRulesDataAsync.rejected,
        (state, action) => {
          state.weekdayMinimumStayRuleItemsDataStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(
        getWeekdayPriceChangeRulesDataAsync.fulfilled,
        (state, action) => {
          state.weekdayPriceChangeRuleItemsData = action.payload;
          state.weekdayPriceChangeRuleItemsDataStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(getWeekdayPriceChangeRulesDataAsync.pending, (state, action) => {
        state.weekdayPriceChangeRuleItemsDataStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getWeekdayPriceChangeRulesDataAsync.rejected,
        (state, action) => {
          state.weekdayPriceChangeRuleItemsDataStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(
        getWeekdayCheckInAndCheckOutTimesDataAsync.fulfilled,
        (state, action) => {
          state.weekdayCheckInCheckOutTimeItemsData = action.payload;
          state.weekdayCheckInCheckOutTimeItemsDataStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(
        getWeekdayCheckInAndCheckOutTimesDataAsync.pending,
        (state, action) => {
          state.weekdayCheckInCheckOutTimeItemsDataStatus =
            LOADING_STATUS.LOADING;
        },
      )
      .addCase(
        getWeekdayCheckInAndCheckOutTimesDataAsync.rejected,
        (state, action) => {
          state.weekdayCheckInCheckOutTimeItemsDataStatus =
            LOADING_STATUS.FAILED;
        },
      );
  },
});

export const {
  addWeekdayCheckInRestriction,
  removeWeekdayCheckInRestriction,
  addWeekdayCheckOutRestriction,
  removeWeekdayCheckOutRestriction,
  updateCopyableForWeekdayCheckInRestrictionData,
  updateCopyableForWeekdayCheckOutRestrictionData,
  updateActiveForWeekdayCheckInRestrictionData,
  updateActiveForWeekdayCheckOutRestrictionData,
  addWeekdayMinimumStayRestriction,
  updateWeekdayMinimumStayRestriction,
  removeWeekdayMinimumStayRestriction,
  updateWeekdayMinimumStayRuleItemsData,
  addWeekdayPriceChangeRule,
  updateWeekdayPriceChangeRule,
  removeWeekdayPriceChangeRule,
  addWeekdayCheckInTime,
  addWeekdayCheckOutTime,
  updateWeekdayCheckInTime,
  updateWeekdayCheckOutTime,
  removeWeekdayCheckInTime,
  removeWeekdayCheckOutTime,
  addWeekdayCheckInTimeByDuration,
  addWeekdayCheckOutTimeByDuration,
  removeWeekdayCheckInTimeByDuration,
  removeWeekdayCheckOutTimeByDuration,
} = seasonRateWeekdayRulesSlice.actions;

export const selectWeekdayCheckInRuleItems = (state: RootState) =>
  state.seasonRateWeekdayRules.weekdayCheckInRuleItems;

export const selectWeekdayCheckOutRuleItems = (state: RootState) =>
  state.seasonRateWeekdayRules.weekdayCheckOutRuleItems;

export const selectWeekdayMinimumStayRuleItemsData = (state: RootState) =>
  state.seasonRateWeekdayRules.weekdayMinimumStayRuleItemsData;

export const selectWeekdayPriceChangeRuleItemsData = (state: RootState) =>
  state.seasonRateWeekdayRules.weekdayPriceChangeRuleItemsData;

export const selectWeekdayCheckInRuleItemsLoadingStatus = (state: RootState) =>
  state.seasonRateWeekdayRules.weekdayCheckInRuleItemsStatus;

export const selectWeekdayCheckOutRuleItemsLoadingStatus = (state: RootState) =>
  state.seasonRateWeekdayRules.weekdayCheckOutRuleItemsStatus;

export const selectWeekdayMinimumStayRuleItemsDataLoadingStatus = (
  state: RootState,
) => state.seasonRateWeekdayRules.weekdayMinimumStayRuleItemsDataStatus;

export const selectWeekdayPriceChangeRuleItemsDataLoadingStatus = (
  state: RootState,
) => state.seasonRateWeekdayRules.weekdayPriceChangeRuleItemsDataStatus;

export const selectWeekdayCheckInCheckOutTimeItemsData = (state: RootState) =>
  state.seasonRateWeekdayRules.weekdayCheckInCheckOutTimeItemsData;

export const selectWeekdayCheckInAndCheckOutTimeItemsDataLoadingStatus = (
  state: RootState,
) => state.seasonRateWeekdayRules.weekdayCheckInCheckOutTimeItemsDataStatus;

export default seasonRateWeekdayRulesSlice.reducer;
